/** @jsxImportSource @emotion/react */
import { useLayers } from '@app/contexts/LayerContext'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { useTutorialContext } from '@app/contexts/TutorialContext'
import { css } from '@emotion/react'
import { Box, useTheme } from '@mui/material'
import { updateMapStateProperty } from '@app/redux/riskMap/riskMapSlice'
import { RootState } from '@app/redux/store'
import { GeocoderButton } from '@app/components/Atoms/Geocoder'
import { Modal } from '@app/components/Atoms/Modal'
import { BaseMapButton } from '@app/components/Pages/Map/BaseMapButton'
import { MapLegends } from '@app/components/Pages/Map/MapLegends'
import { LegendSection } from '@app/components/Molecules/Legend'
import { RiskMapTools } from '@app/components/Pages/Map/MapTools/MapTools'
import { ScenariosByAsset } from '@app/components/Property/types'
import { appLink } from '@app/config/appLink'
import apiClient from '@app/config/axios.config'
import { usePreferences } from '@app/hooks/usePreferences'
import { clientLocationAtom } from '@app/state/clientsAtom'
import { useAtom, useAtomValue } from 'jotai'
import mapboxgl, { Map, MapboxGeoJSONFeature, MapLayerMouseEvent } from 'mapbox-gl'
import { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import ReactDOMServer from 'react-dom/server'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { ThreeDButton } from '@app/components/Molecules/ThreeDButton/ThreeDButton'
import { GenericMapView } from '@app/components/Molecules/MapView'
import {
  selectionMeasurementContainer,
  selectionMeasurementHeader,
} from '@app/components/Molecules/MapView/GenericMapView/GenericMapView.styles'
import {
  addSelectedMarkerToMap,
  getCenterOfFeature,
} from '@app/components/Molecules/MapView/GenericMapView/GenericMapView.utilities'
import { AddLayersDialog } from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog'
import {
  mapBottomLeftContainer,
  mapBottomRightContainer,
  mapOverlayContainer,
  mapTopRightContainer,
} from '@app/components/Molecules/MapView/RiskMapView/RiskMapView.styles'
import {
  getAssetAndHazardDetailsForSelectedFeature,
  getHazardInfoForSelectedFeature,
  getInformationDetailsForSelectedFeature,
  getLayersByType,
} from '@app/components/Molecules/MapView/RiskMapView/RiskMapView.utilities'
import { RiskMapTooltip } from '@app/components/Molecules/MapView/RiskMapView/Tooltip/RiskMapTooltip'
import { addLayersDialogAtom } from '@app/state/layoutAtom'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'
import { useFetchLiveLayers } from '../hooks/useFetchLiveLayers'
import { LiveFeed, LiveLayer } from '../Live.types'

import { LivePopup } from './Live.Popup'
import { useTransformLiveLayers } from '../hooks/useTransformLiveLayers'
import { liveLegendMapOuter, liveMapOuter } from '../styles/LiveLayersMap.styles'
import { PUBLIC_ASSETS_BASE_URL } from '@app/app-constants'
import { getSvgDimensions } from '@app/modules/Live/Live.utils'
import { buildHoverProperties } from './Live.MapView.utils'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

export const LiveMapView = ({ flyToClientLocation = true }: { flyToClientLocation?: boolean }) => {
  mapboxgl.accessToken = MAPBOX_TOKEN
  const theme = useTheme()
  const mapContext = useMap()
  const location = useLocation()
  const dispatch = useDispatch()
  const [openAddLayersSelectionDialog, setOpenAddLayersSelectionDialog] =
    useAtom(addLayersDialogAtom)
  const { data: fetchedLiveFeeds } = useFetchLiveLayers()
  const { transformLiveLayerToMapboxLayers } = useTransformLiveLayers()
  const {
    map,
    setMapInstance,
    selectFeature,
    deselectFeature,
    regionMasks,
    showAddLayersModalTab,
    setShowAddLayersModalTab,
    mapPaddingRef,
    handleSetClientLocation,
  } = mapContext
  const { term_preference } = usePreferences()
  const { liveData, activeLiveLayers, setLiveMapSelected, setLiveLayers, setSelectedLiveFeatures } =
    useLiveContext()
  const {
    layers,
    drawAreas,
    legendsData,
    selectedMarker,
    selectedFeature,
    canInteractWithLayers,
    layerData,
  } = useSelector((state: RootState) => state.riskMap)
  const selectedMapboxGeoJsonFeature = selectedFeature as MapboxGeoJSONFeature
  const { visibleAssets, prepareAndUpdateLayers } = useLayers()
  const clientLocation = useAtomValue(clientLocationAtom)
  const mapOverlayRef = useRef<HTMLDivElement>(null)
  const showSearchAddress = location.pathname.includes(appLink.risk.path)
  const previousMouseEvents = useRef<{ [key: string]: (e: MapLayerMouseEvent) => void }>({})
  const mouseEventsLayerIDTracker = useRef<string[]>([])

  const tooltipRef = useRef(new mapboxgl.Popup({ closeButton: false }))
  const assetLayers = useMemo(() => getLayersByType(layers, 'asset'), [layers])
  const hazardLayers = useMemo(() => getLayersByType(layers, 'hazard'), [layers])
  const informationLayers = useMemo(() => getLayersByType(layers, 'information'), [layers])

  const tooltipTimeoutRef = useRef<number | null>(null)
  const requestSeqRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const liveDataRef = useRef<Record<string, GeoJSON.FeatureCollection | null>>({})
  // Deduplicate live click events: the handler fires once per layer, so store the
  // last processed DOM event timestamp to skip re-entrant calls for the same click.
  const lastLiveClickTimestamp = useRef<number>(-1)

  const cancelTooltipTimer = () => {
    if (tooltipTimeoutRef.current !== null) {
      clearTimeout(tooltipTimeoutRef.current)
      tooltipTimeoutRef.current = null
    }
  }
  const tooltipFeature = useRef<MapboxGeoJSONFeature | undefined>()

  useEffect(() => {
    setLiveMapSelected(true)
    return () => {
      setLiveMapSelected(false)
    }
  }, [setLiveMapSelected])

  useEffect(() => {
    if (fetchedLiveFeeds) {
      setLiveLayers(
        fetchedLiveFeeds.data.map(
          (layer: LiveFeed): LiveLayer => ({
            id: layer.id,
            display_name: layer.title,
            visible: true,
            layerType: 'live',
            assetTag: layer.id,
            tilesets: [],
            helpFileName: '',
            type: layer.id,
            icon: '',
            color: '',
            interactivityDisabled: false,
            url: layer.url,
            feedType: layer.type,
            styles: layer.styles,
            proxy_url: layer.proxy_url,
            live_legend: layer.legend,
            legend: '',
            feature_properties: layer.feature_properties,
            icons: layer.icons,
            last_updated_config: layer.last_updated,
          }),
        ),
      )
    }
  }, [fetchedLiveFeeds, setLiveLayers])

  function controlMouseEvents(map: Map) {
    if (!canInteractWithLayers) {
      removeMouseEvents(map)
      return
    }

    const rootSelectedLayerId = selectedMapboxGeoJsonFeature?.layer?.id
      .replace('-zoomed-out', '')
      .replace('-extrusion', '')

    if (
      selectedMarker &&
      selectedMapboxGeoJsonFeature &&
      rootSelectedLayerId &&
      layers.map((l) => l.id).includes(rootSelectedLayerId)
    ) {
      addSelectedMarkerToMap({
        map,
        coordinates: [selectedMarker.longitude, selectedMarker.latitude],
        colour: theme.palette.primary.main,
      })
    }

    const lookupTileSetIds = hazardLayers.reduce((acc, layer) => {
      if (layer.infoLookupTileset?.length) {
        for (const lookupTileSet of layer.infoLookupTileset) {
          if (lookupTileSet.id) acc.push(lookupTileSet.id)
        }
      }
      return acc
    }, [] as string[])

    if (Object.keys(visibleAssets).length >= 1) {
      addMouseEvents(map, [
        ...Object.keys(visibleAssets),
        ...informationLayers.map((layer) => layer.id),
        ...lookupTileSetIds,
      ])
    } else if (
      assetLayers.length >= 1 ||
      lookupTileSetIds.length >= 1 ||
      (informationLayers.length >= 1 && drawAreas.length === 0 && !regionMasks) ||
      activeLiveLayers.length >= 1
    ) {
      addMouseEvents(map, [
        ...assetLayers.map((layer) => layer.type),
        ...informationLayers.map((layer) => layer.id),
        ...lookupTileSetIds,
      ])
    }
  }

  function removeMouseEvents(map: Map) {
    const existingLayers = map
      .getStyle()
      .layers.filter((layer) => layer.id.includes('urbanintelligence'))
      .filter((layer) => !layer.id.includes('-line'))

    // both existingLayers and mouseEventsLayerIDTracker are necessary to remove mouse events
    existingLayers.forEach((layer) => {
      if (!layer) return
      map.off('mouseenter', layer.id, previousMouseEvents.current['mouseenter'])
      map.off('mousemove', layer.id, previousMouseEvents.current['mousemove'])
      map.off('mouseleave', layer.id, previousMouseEvents.current['mouseleave'])
      map.off('click', layer.id, previousMouseEvents.current['click'])
    })
    mouseEventsLayerIDTracker.current.forEach((layerId) => {
      map.off('mouseenter', layerId, previousMouseEvents.current['mouseenter'])
      map.off('mousemove', layerId, previousMouseEvents.current['mousemove'])
      map.off('mouseleave', layerId, previousMouseEvents.current['mouseleave'])
      map.off('click', layerId, previousMouseEvents.current['click'])
    })
    mouseEventsLayerIDTracker.current = []
  }

  function addMouseEvents(map: Map, visibleAssetIds: string[]) {
    if (!canInteractWithLayers) return
    const popup = tooltipRef.current
    if (!popup) return

    const mapStyle = map.getStyle()
    if (!mapStyle) return

    const existingLayers = mapStyle.layers.filter(
      (layer) =>
        (layer.id.includes('urbanintelligence') && !layer.id.includes('-line')) ||
        layer.id.startsWith('live-'),
    )

    const isLayerInteractivityDisabled = (layer: { id: string; source?: string }) => {
      for (const assetLayer of assetLayers) {
        const tileset = assetLayer.tilesets.find((iLayer) => iLayer.id === layer.source)
        if (tileset) {
          return assetLayer.interactivityDisabled
        }
      }
      for (const infoLayer of informationLayers) {
        const tileset = infoLayer.tilesets.find((iLayer) => iLayer.id === layer.source)
        if (tileset) {
          return infoLayer.interactivityDisabled
        }
      }
      for (const hazardLayer of hazardLayers.filter((layer) => layer.infoLookupTileset?.length)) {
        const tileset = hazardLayer.tilesets.find((hLayer) => hLayer.id === layer.id)
        if (tileset) {
          return hazardLayer.interactivityDisabled
        }
      }

      return false
    }

    const layerIdMap = layers.map((layer) => {
      if (layer.infoLookupTileset?.length) return layer?.infoLookupTileset?.[0]?.id
      if (layer.tilesets?.[0]) return layer.tilesets[0].id
      if (layer.id) return layer.id
      return ''
    })

    const riskInteractiveLayers = visibleAssetIds
      .map((assetId) => {
        return existingLayers.filter((layer) => {
          // Some layers have a separate layer when zoomed out such as an icon layer where it uses points if zoomed out
          return (
            layer.id.includes(assetId) ||
            layer.id.includes(`${assetId}-zoomed-out`) ||
            layer.id.includes(`${assetId}-extrusion`)
          )
        })
      })
      .flat()
      .sort((a, b) => {
        return layerIdMap.findIndex((id) => id === a.id) - layerIdMap.findIndex((id) => id === b.id)
      })

    const liveInteractiveLayers = existingLayers.filter((layer) => layer.id.startsWith('live-'))

    const interactiveLayers = [...riskInteractiveLayers, ...liveInteractiveLayers]

    const hasRiskElementAtPoint = (point: mapboxgl.Point) =>
      riskInteractiveLayers.length > 0 &&
      map.queryRenderedFeatures(point, { layers: riskInteractiveLayers.map((l) => l.id) }).length >
        0

    // Func to check if this feature's layer is higher priority than the one already shown
    // If true, that timeout will be replaced with a new one loading this layer
    // If no other layer is loading, this one will be loaded
    const isFeatureHigherPriorityThanAlreadyLoadingFeature = (feature: MapboxGeoJSONFeature) => {
      if (tooltipFeature.current == undefined) return true
      // Risk features always take priority over live features — live layer IDs are not in layerIdMap
      // so the index comparison would yield -1 and incorrectly block the risk path
      if (
        tooltipFeature.current.layer?.id.startsWith('live-') &&
        !feature.layer?.id.startsWith('live-')
      )
        return true

      const layerId = feature.layer?.id.replace('-zoomed-out', '').replace('-extrusion', '') // Some layers are split into a zoomed in or zoomed out layer such as an icon layer.
      // use layers to sort instead of visibleAssetIds as visibleAssetIds is not in the correct order

      return (
        layerIdMap.findIndex((id) => id === layerId) <=
        layerIdMap.findIndex((id) => id === tooltipFeature.current?.layer?.id)
      )
    }

    const updateTooltip = (e: MapLayerMouseEvent) => {
      if (!e.features?.length) return

      const feature = e.features[0]
      const isLiveFeature = feature.layer?.id.startsWith('live-')

      if (isLiveFeature) {
        const liveLayer = activeLiveLayers.find((l) => feature.layer?.id.includes(l.id))
        if (!liveLayer) return
        if (liveLayer.interactivityDisabled) return

        const isPolygon = ['Polygon', 'MultiPolygon'].includes(feature.geometry.type)
        if (isPolygon && hasRiskElementAtPoint(e.point)) {
          // Entering risk asset territory — cancel any pending live timer and hand off to risk path
          if (tooltipFeature.current?.layer?.id.startsWith('live-')) {
            if (requestSeqRef.current !== null) {
              clearTimeout(requestSeqRef.current)
              requestSeqRef.current = null
            }
            tooltipFeature.current = undefined
          }
          return
        }

        // Live feature takes priority — cancel any pending risk tooltip timer
        if (requestSeqRef.current !== null) {
          clearTimeout(requestSeqRef.current)
        }

        tooltipFeature.current = feature
        map.getCanvas().style.cursor = 'pointer'
        popup.setLngLat(e.lngLat)

        const props = (feature.properties ?? {}) as { [name: string]: string | number }

        requestSeqRef.current = setTimeout(async () => {
          if (!tooltipFeature.current) return popup.remove()

          const orderedProperties = buildHoverProperties(props, liveLayer)

          const iconName = (props.icon as string) || (props.Impact as string) || undefined
          let iconURL = undefined

          if (iconName) {
            const candidateURL = `${PUBLIC_ASSETS_BASE_URL}/icons/live_feeds/${liveLayer.id}/${iconName}.svg`
            iconURL = await fetch(candidateURL)
              .then((r) => (r.ok ? candidateURL : undefined))
              .catch(() => undefined)
          }

          // Count distinct alerts at this point (same dedup as the click handler).
          const seen = new Set<string>()
          const stackedCount = map
            .queryRenderedFeatures(e.point, {
              layers: liveInteractiveLayers.map((layer) => layer.id),
            })
            .filter((mapboxFeature) => {
              const properties = mapboxFeature.properties ?? {}
              const stableId = properties.identifier ?? properties.id ?? null
              const key = stableId
                ? `${mapboxFeature.layer?.source}::${stableId}`
                : `${mapboxFeature.layer?.source}::${JSON.stringify(properties)}`
              if (seen.has(key)) return false
              seen.add(key)
              return true
            }).length

          popup
            .setLngLat(e.lngLat)
            .setMaxWidth('300px')
            .setHTML(
              ReactDOMServer.renderToString(
                LivePopup({
                  props: orderedProperties,
                  layerId: liveLayer.id,
                  layerName: liveLayer.display_name,
                  primaryColour: props.color as string | undefined,
                  iconURL,
                  stackedCount,
                }),
              ),
            )
            .addTo(map)
        }, 1000)
        return
      }

      // Risk feature path
      // Suppress if a non-polygon live feature is at this point (live tooltip takes priority)
      if (liveInteractiveLayers.length > 0) {
        const liveAtPoint = map.queryRenderedFeatures(e.point, {
          layers: liveInteractiveLayers.map((l) => l.id),
        })
        if (liveAtPoint.some((f) => !['Polygon', 'MultiPolygon'].includes(f.geometry.type))) return
      }

      // Only show tooltip if the feature has a value or asset_id
      // Which is the sign of an info feature that has a description to show or if it has an asset_id it likely has a tooltip sentence
      const hasSomeDescription = e.features.some((feature) => {
        return (
          (feature.properties && feature.properties.length > 0) ||
          feature.properties?.value ||
          feature.properties?.display_value ||
          feature.properties?.hover_comment ||
          feature.properties?.asset_id !== null ||
          feature.properties?.asset_id !== undefined
        )
      })

      if (!hasSomeDescription) return

      const interactivityIsDisabled = e.features.some((feature) => {
        const layer = interactiveLayers.find(
          (layer: mapboxgl.Layer) => layer?.source === feature.layer?.source,
        )
        const hazardLayerLookup = hazardLayers.find((hLayer) => {
          return (
            hLayer.infoLookupTileset?.length &&
            hLayer.infoLookupTileset.some((tileset) => tileset.id === feature.layer?.id)
          )
        })
        let flag: boolean | undefined = false
        if (hazardLayerLookup) {
          flag = isLayerInteractivityDisabled(hazardLayerLookup)
        } else if (layer) flag = isLayerInteractivityDisabled(layer)
        return flag
      })
      if (interactivityIsDisabled) return
      const hLayer = hazardLayers.find((hLayer) => {
        return (
          hLayer.infoLookupTileset?.length &&
          hLayer.infoLookupTileset.some((tileset) =>
            e.features?.some((feature: MapboxGeoJSONFeature) => feature.layer?.id === tileset.id),
          )
        )
      })
      let multiInfoLookupResults = null
      if (hLayer?.infoLookupTileset?.length ?? 0 > 1) {
        const res = map.queryRenderedFeatures(e.point)
        multiInfoLookupResults = res
      }

      map.getCanvas().style.cursor = 'pointer'

      popup.setLngLat(e.lngLat)
      popup.setMaxWidth('800px')

      // Return if the feature is already being hovered over.
      if (tooltipFeature.current && tooltipFeature.current.id === e.features[0].id) return

      // Return if the feature is lower priority than the one already loading
      if (!isFeatureHigherPriorityThanAlreadyLoadingFeature(e.features[0])) return

      // Set the feature as currently shown
      tooltipFeature.current = e.features[0]

      // Fade out outdated tooltip content (this typecast is safe btw)
      const content = document.getElementsByClassName('tooltip-container')[0] as HTMLElement
      if (content) content.style.color = '#AAA'
      if (requestSeqRef.current !== null) {
        clearTimeout(requestSeqRef.current)
      }

      requestSeqRef.current = setTimeout(async () => {
        if (!tooltipFeature.current) return popup.remove()
        const details = await getAssetAndHazardDetailsForSelectedFeature(
          tooltipFeature.current,
          assetLayers,
          hazardLayers,
          legendsData,
        )
        const information = getInformationDetailsForSelectedFeature(
          tooltipFeature.current,
          informationLayers,
        )
        const hazardInformation = getHazardInfoForSelectedFeature(
          tooltipFeature.current,
          hazardLayers,
        )

        // for Mapbox NIWA data storage compression by splitting scenarios into separate layers
        if (hazardInformation && !hazardInformation.value && multiInfoLookupResults) {
          if (hLayer && hLayer.assetTag) {
            const infoLookupRes = multiInfoLookupResults.find((feature) =>
              hazardLayers.some(
                (layer) =>
                  feature.properties &&
                  Object.keys(feature.properties).some((property) => property === layer.assetTag),
              ),
            )
            if (!infoLookupRes || !infoLookupRes.properties) return
            hazardInformation.value = infoLookupRes.properties[hLayer.assetTag]
          }
        }
        if (!details && !information && !hazardInformation) return popup.remove()

        const asset = details?.asset
        const hazards = details?.hazards

        const scenariosByAsset: ScenariosByAsset[] = asset
          ? (await apiClient.get(`/hazard/scenarios/${asset.type}/${asset.id}`)).data
          : []
        const knownRiskScenarioIds = scenariosByAsset
          .filter((s) => s.is_exposed == 'true')
          .map((scenario) => scenario.hazard_scenario)

        const knownRiskIcons: Set<string> = new Set<string>()
        knownRiskScenarioIds.forEach((knownRiskScenarioId) => {
          const knownRisk = layerData?.hazardDialogData.hazardDetails.find((hazardDetail) =>
            hazardDetail.scenarios.find((s) => s.assetTag == knownRiskScenarioId),
          )

          // Hacky way to check if element is exposed to a mapped hazard as knownRisk diesn't have any useful ids
          // and seeing as this is only for CCC I'd rather not change the API to do a better job.
          // If the scenario id format changes the only risk is an extra icon in the list.
          const riskIsOnMap =
            Array.isArray(hazards) &&
            hazards?.some((hazard) => knownRiskScenarioId.startsWith(`h${hazard.hazard_id}`))
          if (knownRisk && !riskIsOnMap) knownRiskIcons.add(knownRisk.icon)
        })

        const mapToolTip = RiskMapTooltip({
          asset,
          hazards,
          information: information || hazardInformation,
          mainColour: theme.palette.primary.main,
          prefer_hazard_term: term_preference.hazard,
          prefer_risk_term: term_preference.risk,
          knownRiskIcons: Array.from(knownRiskIcons),
        })

        if (!mapToolTip) return
        popup.setHTML(ReactDOMServer.renderToString(mapToolTip))
        popup.addTo(map)
      }, 400)
    }

    // MOUSE EVENTS
    const mouseEnterEvent = function (e: MapLayerMouseEvent) {
      if (!e) return
      if (mapContext.interactive) updateTooltip(e)
    }

    const mouseMoveEvent = (e: MapLayerMouseEvent) => {
      if (mapContext.interactive) {
        popup.remove()
        updateTooltip(e)
      }
    }

    const mouseLeaveEvent = (_e: MapLayerMouseEvent) => {
      cancelTooltipTimer()
      if (requestSeqRef.current !== null) {
        clearTimeout(requestSeqRef.current)
        requestSeqRef.current = null
      }
      tooltipFeature.current = undefined
      map.getCanvas().style.cursor = ''
      popup.remove()
    }

    let selectFeatureTimeout: null | NodeJS.Timeout = null
    const mouseClickEvent = async (mouseEvent: MapLayerMouseEvent) => {
      if (typeof mouseEvent.features === 'undefined') return
      const feature = mouseEvent.features[0]
      if (!feature) return

      const isLiveFeature = feature.layer?.id.startsWith('live-')

      if (isLiveFeature) {
        const isPolygon = ['Polygon', 'MultiPolygon'].includes(feature.geometry.type)
        if (isPolygon && hasRiskElementAtPoint(mouseEvent.point)) return

        // The handler fires once per registered layer. Deduplicate so we only
        // process once per user click even when several live layers overlap.
        const clickTimeStamp = mouseEvent.originalEvent.timeStamp
        if (lastLiveClickTimestamp.current === clickTimeStamp) return
        lastLiveClickTimestamp.current = clickTimeStamp

        popup.remove()

        // Collect every live feature at the click point in rendering order (top first).
        const allLiveFeaturesAtPoint = map.queryRenderedFeatures(mouseEvent.point, {
          layers: liveInteractiveLayers.map((l) => l.id),
        })

        // Live feeds push multiple layers (fill, line, symbol, etc) into the same source
        // for each alert. queryRenderedFeatures returns all when clicking the alert,
        // producing duplicate entries. Deduplicate by (source, identifier), falling
        // back to a full property hash when no stable identifier property is present.
        const seen = new Set<string>()
        const liveFeaturesWithLayers = allLiveFeaturesAtPoint
          .map((mapboxFeature) => {
            const liveLayer = activeLiveLayers.find((layer) =>
              mapboxFeature.layer?.id.includes(layer.id),
            )
            return liveLayer ? { feature: mapboxFeature, liveLayer } : null
          })
          .filter(
            (
              liveFeatureWithLayer,
            ): liveFeatureWithLayer is NonNullable<typeof liveFeatureWithLayer> =>
              liveFeatureWithLayer !== null,
          )
          .filter(({ feature: mapboxFeature }) => {
            const properties = mapboxFeature.properties ?? {}
            const stableId = properties.identifier ?? properties.id ?? null
            const key = stableId
              ? `${mapboxFeature.layer?.source}::${stableId}`
              : `${mapboxFeature.layer?.source}::${JSON.stringify(properties)}`
            if (seen.has(key)) return false
            seen.add(key)
            return true
          })

        if (liveFeaturesWithLayers.length === 0) return

        const coordinates: [number, number] = [mouseEvent.lngLat.lng, mouseEvent.lngLat.lat]

        deselectFeature()
        // Select the topmost feature so the side-panel tab activates.
        selectFeature(
          liveFeaturesWithLayers[0].feature,
          liveFeaturesWithLayers[0].liveLayer,
          'selection',
        )
        setSelectedLiveFeatures(liveFeaturesWithLayers)
        addSelectedMarkerToMap({
          map,
          coordinates: coordinates as mapboxgl.LngLatLike,
          colour: theme.palette.primary.main,
        })
        dispatch(
          updateMapStateProperty({
            selectedMarker: { longitude: coordinates[0], latitude: coordinates[1] },
          }),
        )
        return
      }

      // Risk feature click — clear any live multi-selection
      setSelectedLiveFeatures([])
      let coordinates = [mouseEvent.lngLat.lng, mouseEvent.lngLat.lat]
      const featureCenter = getCenterOfFeature(feature)
      if (featureCenter) {
        coordinates = featureCenter
      }

      const selectedAsset = assetLayers.find(
        (assetLayer) => assetLayer.tilesets[0].id === feature.layer?.source,
      )
      const selectedContextLayer = informationLayers.find(
        (infoLayer) => infoLayer.tilesets[0].id === feature.layer?.source,
      )

      if (!selectFeatureTimeout) {
        // If feature already selected and not on selection tab
        // it wouldn't return to selection tab without deselecting it first.
        deselectFeature()
        selectFeatureTimeout = setTimeout(() => {
          selectFeatureTimeout = null
          let successfulSelect = false
          if (selectedContextLayer) {
            successfulSelect = selectFeature(feature, selectedContextLayer, 'selection')
          }
          if (selectedAsset) {
            successfulSelect = selectFeature(feature, selectedAsset, 'selection')
          }
          if (successfulSelect) {
            addSelectedMarkerToMap({
              map,
              coordinates: coordinates as mapboxgl.LngLatLike,
              colour: theme.palette.primary.main,
            })

            dispatch(
              updateMapStateProperty({
                selectedMarker: {
                  longitude: coordinates[0],
                  latitude: coordinates[1],
                },
              }),
            )
          }
          if (requestSeqRef.current !== null) {
            clearTimeout(requestSeqRef.current)
          }
          tooltipFeature.current = undefined
          tooltipRef.current.remove()
        }, 100)
      }
    }

    const mouseEvents: { [key: string]: (e: MapLayerMouseEvent) => void } = {
      mouseenter: mouseEnterEvent,
      mousemove: mouseMoveEvent,
      mouseleave: mouseLeaveEvent,
      click: mouseClickEvent,
    }

    Object.keys(mouseEvents).forEach((eventName) => {
      if (previousMouseEvents.current[eventName] == undefined) {
        previousMouseEvents.current[eventName] = mouseEvents[eventName]
      }
    })

    // Clear existing events and add new ones
    mouseEventsLayerIDTracker.current.forEach((layerId) => {
      map.off('mouseenter', layerId, previousMouseEvents.current['mouseenter'])
      map.off('mousemove', layerId, previousMouseEvents.current['mousemove'])
      map.off('mouseleave', layerId, previousMouseEvents.current['mouseleave'])
      map.off('click', layerId, previousMouseEvents.current['click'])
    })
    mouseEventsLayerIDTracker.current = []

    Object.keys(mouseEvents).forEach((eventName) => {
      previousMouseEvents.current[eventName] = mouseEvents[eventName]
    })

    interactiveLayers.forEach((layer) => {
      if (!layer) return
      map.on('click', layer.id, previousMouseEvents.current['click'])
      map.on('mouseenter', layer.id, previousMouseEvents.current['mouseenter'])
      map.on('mousemove', layer.id, previousMouseEvents.current['mousemove'])
      map.on('mouseleave', layer.id, previousMouseEvents.current['mouseleave'])
      mouseEventsLayerIDTracker.current.push(layer.id)
    })
  }

  useEffect(() => {
    if (clientLocation && handleSetClientLocation) {
      handleSetClientLocation(clientLocation)
    }
  }, [clientLocation, handleSetClientLocation])

  useEffect(() => {
    async function addMapLayersAndEvents() {
      await prepareAndUpdateLayers({ layers, applyVulnerabilityData: false })
      if (map) controlMouseEvents(map)
    }
    addMapLayersAndEvents()

    const hidePopupOnZoom = () => tooltipRef.current.remove()
    map?.on('zoom', hidePopupOnZoom)
    return () => {
      map?.off('zoom', hidePopupOnZoom)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  useEffect(() => {
    if (!map || !selectedMapboxGeoJsonFeature || !layers) return

    const rootSelectedLayerId = selectedMapboxGeoJsonFeature?.layer?.id
      .replace('-zoomed-out', '')
      .replace('-extrusion', '')

    if (!rootSelectedLayerId || !layers.map((l) => l.id).includes(rootSelectedLayerId)) {
      deselectFeature()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers])

  useEffect(() => {
    if (!map) return
    tooltipRef.current.remove()
    controlMouseEvents(map)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, visibleAssets, selectedMarker, canInteractWithLayers])

  // Re-register mouse events when interactivityDisabled changes without triggering
  // the full layer teardown/rebuild that would cause a visual flicker.
  useEffect(() => {
    if (!map) return
    controlMouseEvents(map)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLiveLayers])

  useEffect(() => {
    return () => cancelTooltipTimer()
  }, [])

  // Add setShowAddLayersModalTab to TutorialContext
  const { openAddLayersModal } = useTutorialContext()
  useEffect(() => {
    if (openAddLayersModal) {
      setShowAddLayersModalTab('live')
    } else {
      setShowAddLayersModalTab('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAddLayersModal])

  // Structural key: only changes when layers are added/removed or visibility toggles.
  // interactivityDisabled and other property-only changes leave this stable, preventing
  // the setup effect from tearing down and rebuilding the Mapbox layers (which causes flicker).
  const activeLayerStructureKey = activeLiveLayers
    .map((layer) => `${layer.id}:${layer.visible}`)
    .join(',')

  useEffect(() => {
    if (!map) return

    const setupLiveLayers = () => {
      activeLiveLayers.forEach((layer) => {
        const sourceId = `live-feed-${layer.id}`

        if (layer.visible) {
          if (layer.icons) {
            layer.icons.icons.forEach((iconId) => {
              fetch(`${PUBLIC_ASSETS_BASE_URL}/icons/live_feeds/${layer.id}/${iconId}.svg`).then(
                async (response) => {
                  const iconSvg = await response.text()
                  const { width, height } = getSvgDimensions(iconSvg)
                  const svgBitmap = new Image(width, height)
                  svgBitmap.onload = () => {
                    const iconName = `${layer.id}-${iconId}`.replaceAll(' ', '')
                    if (!map.hasImage(iconName)) map.addImage(iconName, svgBitmap)
                  }
                  svgBitmap.src = 'data:image/svg+xml;base64,' + btoa(iconSvg)
                },
              )
            })
          }

          if (!map.getSource(sourceId)) {
            map.addSource(sourceId, {
              type: 'geojson',
              data: liveDataRef.current[layer.id] ?? { type: 'FeatureCollection', features: [] },
            })
            const liveMapboxLayers = transformLiveLayerToMapboxLayers(layer)
            liveMapboxLayers.forEach((liveMapboxLayer) => {
              map.addLayer(liveMapboxLayer)
            })
          }
        } else {
          const liveMapboxLayers = transformLiveLayerToMapboxLayers(layer)
          liveMapboxLayers.forEach(({ id: liveLayerId }) => {
            if (map.getLayer(liveLayerId)) map.removeLayer(liveLayerId)
          })
          if (map.getSource(sourceId)) map.removeSource(sourceId)
        }
      })

      // Re-register all events so the unified handlers cover newly added live layers
      controlMouseEvents(map)
    } // end setupLiveLayers

    if (map.isStyleLoaded()) {
      setupLiveLayers()
    } else {
      map.once('style.load', setupLiveLayers)
    }

    return () => {
      map.off('style.load', setupLiveLayers)
      // Style replaced by base map change — Mapbox already wiped the layers/sources
      if (!map.isStyleLoaded()) return
      activeLiveLayers.forEach((layer) => {
        const sourceId = `live-feed-${layer.id}`
        const liveMapboxLayers = transformLiveLayerToMapboxLayers(layer)
        liveMapboxLayers.forEach(({ id: liveLayerId }) => {
          if (map.getLayer(liveLayerId)) map.removeLayer(liveLayerId)
        })
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    activeLayerStructureKey,
    theme,
    dispatch,
    selectFeature,
    deselectFeature,
    transformLiveLayerToMapboxLayers,
  ])

  useEffect(() => {
    liveDataRef.current = liveData
  }, [liveData])

  useEffect(() => {
    if (!map) return
    Object.entries(liveData).forEach(([layerId, data]) => {
      if (!data) return
      const source = map.getSource(`live-feed-${layerId}`) as mapboxgl.GeoJSONSource | undefined
      if (source) source.setData(data)
    })
    // Stale selection: when the underlying alert data refreshes, the selected
    // features may no longer exist at the same position, so clear the selection.
    deselectFeature()
    setSelectedLiveFeatures([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, liveData])

  const legendIds = useMemo(() => {
    const visibleLayers = layers.filter((layer) => layer.visible)
    const visibleLayerLegends = visibleLayers.map((layer) => layer.legend)
    return visibleLayerLegends.filter((legend) => legend !== '')
  }, [layers])

  const liveLegends = useMemo(() => {
    return activeLiveLayers
      .filter((layer) => layer.visible !== false && layer.styles)
      .flatMap((layer) => {
        if (!layer.styles) return []

        return [
          {
            id: layer.id,
            title: layer.display_name,
            sections: [
              {
                type: 'categorical' as const,
                items: (layer.live_legend?.items ?? []).map((item) => ({
                  ...item,
                  logo: item.logo
                    ? `${PUBLIC_ASSETS_BASE_URL}/icons/live_feeds/${layer.id}/${item.logo}`
                    : undefined,
                })),
              },
            ] as LegendSection[],
          },
        ]
      })
  }, [activeLiveLayers])

  const noVulnerabilityAssets = useMemo(() => {
    const noVulnerabilityAssets: string[] = []
    const elements = layers.flatMap((l) => {
      if (l.layerType === 'asset') {
        return l.display_name
      }
      return []
    })
    noVulnerabilityAssets.push(...elements)

    return noVulnerabilityAssets
  }, [layers])

  return (
    <>
      <GenericMapView
        setMapInstance={setMapInstance}
        flyToClientLocation={flyToClientLocation}
        map={map}
        mapContext={mapContext}
        customStyle={css`
          & .mapboxgl-ctrl-top-right .mapboxgl-ctrl {
            margin-top: ${showSearchAddress ? '116px' : '80px'};
          }
        `}
        mapPaddingRef={mapPaddingRef}
        inContainerSlot={
          drawAreas.length ? (
            <div id="selection-measurement" css={selectionMeasurementContainer}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <h6 css={selectionMeasurementHeader}>Selection Details:</h6>
                <span>
                  Area: <span id="selection-calculated-area"></span>
                </span>
                <span>
                  Length: <span id="selection-calculated-length"></span>
                </span>
              </div>
            </div>
          ) : null
        }
        outsideContainerSlot={
          showAddLayersModalTab || openAddLayersSelectionDialog
            ? createPortal(
                <Modal
                  open={!!showAddLayersModalTab || openAddLayersSelectionDialog}
                  onClose={() => {
                    setShowAddLayersModalTab('')
                    setOpenAddLayersSelectionDialog(false)
                  }}
                >
                  <AddLayersDialog
                    onClose={() => {
                      setShowAddLayersModalTab('')
                      setOpenAddLayersSelectionDialog(false)
                    }}
                    liveData={true}
                  />
                </Modal>,
                document.body,
              )
            : null
        }
      />
      <Box ref={mapOverlayRef} css={mapOverlayContainer}>
        {mapContext.interactive && (
          <Box css={mapTopRightContainer}>
            <Box css={liveMapOuter}>
              <RiskMapTools mapOverlayRef={mapOverlayRef} />
              {showSearchAddress && <GeocoderButton map={map} />}
              <ThreeDButton mapContext={mapContext} />
            </Box>
          </Box>
        )}
        <Box css={mapBottomLeftContainer}>
          <BaseMapButton />
        </Box>
        <Box css={[mapBottomRightContainer({ theme }), liveLegendMapOuter]}>
          <MapLegends
            legendIds={legendIds}
            uiBlueLegendTitles={noVulnerabilityAssets}
            liveLegends={liveLegends}
          />
        </Box>
      </Box>
      <Box className="tutorial-map" />
    </>
  )
}
